import { default as React, useContext, useMemo } from "react";
import AppContext from "../../../contexts/AppContext";
import InspectionCertificationContext, {
  InspectionCertificationContextProvider,
} from "../../../contexts/InspectionCertificationContext";
import { InspectionContext } from "../../../contexts/InspectionContext";
import { ProjectContext } from "../../../contexts/ProjectContext";
import RolesContext from "../../../contexts/RolesContext";
import {
  resolveProjectTimezone,
  toCalendarDate,
} from "../../../utils/certDefaultDate";
import {
  CertificationUI,
  getcDOTObservationTemplates,
} from "./CertificationUI";
import NotesForm from "./NotesForm";
import InspectionDocumentUploads from "./InspectionDocumentUploads";
import {
  pendingPhotoCount,
  usePendingPhotoUploads,
} from "../../../hooks/backgroundUploads";

const InspectionCertification = (props: any) => {
  const projectContext: any = useContext(ProjectContext);
  const inspectionContext = useContext(InspectionContext);
  const appContext = useContext(AppContext);
  const templateName = useMemo(
    () => (projectContext.template ? projectContext.template.name : null),
    [projectContext.template],
  );
  const isCcr =
    (appContext.get("constants.ccrTemplates") as string[]).includes(
      templateName,
    ) && inspectionContext.inspection.project.isCcrProject;
  const isPe = (appContext.get("constants.peTemplates") as string[]).includes(
    templateName,
  );

  /**
   * SW2-2239. The minimum date a signature may carry, taken from the bounds the server now resolves
   * per line (`certState.dateBounds`) rather than recomputed here.
   *
   * This replaces the seventh and last copy of the
   * `new Date(doi.getTime() + doi.getTimezoneOffset() * 60000)` idiom, and fixes a real asymmetry: only
   * `inspectorMinDate` ever applied that offset. The compliance minimum was a bare
   * `new Date(certContext.state.minCertificationDate)`, so in every US timezone the picker allowed one
   * calendar day EARLIER than the server's stated minimum — and it ignored
   * `disable_inspection_backdating` entirely, which the inspector minimum did honour.
   *
   * `CertificationUI` takes one minimum per group, so the group's first line supplies it — the same
   * first-line rule the inspection-level date columns use. `toCalendarDate` is the fallback for an older
   * server response that carries no bounds.
   */
  const groupMinDate = (
    certState: any,
    group: 'inspection' | 'compliance' | 'acknowledgement',
  ): Date => {
    const firstLine = certState?.signatureGroups?.[group]?.[0];
    const bound = firstLine && certState?.dateBounds?.[firstLine.type]?.minDate;
    const timezone = resolveProjectTimezone(
      certState?.projectTimezone,
      projectContext?.project?.timezone,
    );

    return (
      toCalendarDate(bound ?? certState?.minCertificationDate, timezone) ??
      (toCalendarDate(new Date(), timezone) as Date)
    );
  };

  const permCheck = useContext(RolesContext).userHasPermission;

  // SW2-2458: signing builds and emails the Inspection Complete report, so wait
  // until every finding photo uploading in the background has reached the server.
  const uploadingPhotos = pendingPhotoCount(
    usePendingPhotoUploads({ inspectionId: inspectionContext?.inspection?.id }),
  );
  const withUploadBlock = (groupOptions: any) => {
    if (!uploadingPhotos || !groupOptions) return groupOptions;

    const message = `Waiting for ${uploadingPhotos} photo${
      uploadingPhotos === 1 ? "" : "s"
    } to finish uploading before signing.`;

    return Object.keys(groupOptions).reduce((blocked: any, group) => {
      const options = groupOptions[group] || {};

      blocked[group] = {
        ...options,
        enabled: false,
        errors: [...(options.errors || []), message],
      };

      return blocked;
    }, {});
  };

  const readOnly = () => {
    if (permCheck("all", "Certify") || permCheck("all", "Sign Off")) {
      return false
    }

    return true
  }

  return (
    <InspectionCertificationContextProvider>
      <InspectionCertificationContext.Consumer>
        {certContext => (
          <>
            <CertificationUI
              Acknowledgement={certContext.state.attestations.acknowledgement.length ? true : false}
              signatureGroups={certContext.state.signatureGroups}
              attestationGroups={certContext.state.attestations}
              isCcr={isCcr}
              isPe={isPe}
              inspectorMinCertificationDate={groupMinDate(
                certContext.state,
                'inspection',
              )}
              minCertificationDate={groupMinDate(
                certContext.state,
                'compliance',
              )}
              onSave={certContext.onSave}
              userDataGroups={certContext.state.userSignatures}
              optionGroups={withUploadBlock(certContext.state.groupOptions)}
              showCertificate={inspectionContext.inspection.inspection_template.has_acknowledgement || certContext.state.userSignatures.acknowledgement.length ? true : false}
              showComplianceCert={inspectionContext.inspection.inspection_template.has_compliance_cert}
              ackName={projectContext.template && projectContext.template.name === 'CDOT' ? "Inspection Certification (optional)" : "Acknowledgement"}
              readOnly={readOnly()}
            />
            <NotesForm
              initialNotes={props.initialNotes}
              inspectionId={inspectionContext.inspection.id}
              handleSuccess={() => certContext.flashSuccess()}
              templates={
                templateName === "CDOT"
                  ? getcDOTObservationTemplates(projectContext.clientName)
                  : undefined
              }
            />
            <InspectionDocumentUploads />
          </>
        )}
      </InspectionCertificationContext.Consumer>
    </InspectionCertificationContextProvider>
  );
};

export default InspectionCertification;
